import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Dropdown, Image } from 'react-bootstrap';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import {
  DEFAULT_LANDING_SECTIONS,
  getVisibleMarketingNavItems,
  mergeLandingSections
} from '../utils/landingSections';
import { BRAND_LOGO_COLOR } from '../utils/brandAssets';
import './Landing.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function sanitizePhone(raw) {
  return String(raw || '').replace(/[^\d]/g, '');
}

const waIcon = (
  <svg className="landing-wa-icon" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/**
 * Unified top bar: landing dark theme, WhatsApp pill, optional login, user menu.
 * @param {'marketing'|'app'} variant — marketing: section anchors; app: home + news + optional dashboard link
 */
function SiteHeader({
  variant = 'marketing',
  activeAppSection = 'dashboard',
  onLogout,
  onAuthUserLoaded,
  onNavTrack
}) {
  const { language, languageSelectorEnabled } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [whatsappBolivia, setWhatsappBolivia] = useState('');
  const [landingSections, setLandingSections] = useState(DEFAULT_LANDING_SECTIONS);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const onAuthUserLoadedRef = useRef(onAuthUserLoaded);
  onAuthUserLoadedRef.current = onAuthUserLoaded;

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/config`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        setWhatsappBolivia(cfg.siteContact?.whatsappBolivia || '');
        setLandingSections(mergeLandingSections(cfg.landingSections));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/auth/me`, { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        setUser(data);
        onAuthUserLoadedRef.current?.(data ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          onAuthUserLoadedRef.current?.(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileMenuOpen]);

  const track = (section, action, label = '') => {
    onNavTrack?.(section, action, label);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const whatsappBoliviaHref =
    sanitizePhone(whatsappBolivia).length > 0
      ? `https://wa.me/${sanitizePhone(whatsappBolivia)}`
      : '';

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      /* ignore */
    }
    setUser(null);
    onAuthUserLoadedRef.current?.(null);
    onLogout?.();
    closeMobileMenu();
    navigate('/', { replace: true });
  };

  const isStaff = user && ['admin', 'manager', 'sales'].includes(user.role);
  const staffDashboardHref = '/app/dashboard';
  const hashPrefix = location.pathname === '/' ? '' : '/';
  const visibleMarketingNav = getVisibleMarketingNavItems(landingSections);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((open) => !open);
  };

  return (
    <>
      <header className="landing-nav">
        <Container fluid className="landing-nav-inner">
          <Link
            to="/"
            className="landing-nav-brand"
            onClick={() => {
              track('nav', 'click_logo');
              closeMobileMenu();
            }}
          >
            <img src={BRAND_LOGO_COLOR} alt="" className="landing-nav-logo-icon" width={36} height={36} aria-hidden />
            {language === 'en' ? (
              <span className="landing-nav-brand-text landing-brand-full">{t('landing.footerBrandFull', language)}</span>
            ) : (
              <span className="landing-nav-brand-text">
                <span className="landing-footer-part-estu">{t('landing.footerWordEstu', language)}</span>{' '}
                <span className="landing-footer-part-mejor">{t('landing.footerWordMejor', language)}</span>{' '}
                <span className="landing-footer-part-opcion">{t('landing.footerWordOpcion', language)}</span>
              </span>
            )}
          </Link>

          <button
            type="button"
            className={`landing-nav-burger d-lg-none ${mobileMenuOpen ? 'is-open' : ''}`}
            aria-expanded={mobileMenuOpen}
            aria-controls="landing-nav-mobile-panel"
            aria-label={t('nav.menuToggleAria', language)}
            onClick={toggleMobileMenu}
          >
            <span className="landing-nav-burger-lines" aria-hidden>
              <span />
              <span />
              <span />
            </span>
          </button>

          {variant === 'marketing' ? (
            <nav className="landing-nav-links" aria-label="Primary">
              {visibleMarketingNav.map((item) => (
                <a key={item.id} href={`${hashPrefix}#${item.id}`} className="landing-nav-link">
                  {t(item.labelKey, language)}
                </a>
              ))}
            </nav>
          ) : (
            <nav className="landing-nav-links" aria-label="Main">
              <Link to="/" className="landing-nav-link" onClick={() => track('nav', 'click_home')}>
                {t('nav.home', language)}
              </Link>
              <Link to="/all-news" className="landing-nav-link" onClick={() => track('nav', 'click_all_news')}>
                {t('nav.allNews', language)}
              </Link>
              {activeAppSection !== 'dashboard' && isStaff && (
                <Link
                  to={staffDashboardHref}
                  className="landing-nav-link landing-nav-dashboard-link"
                  onClick={() => track('nav', 'click_dashboard')}
                >
                  ✨ {t('nav.blogAdmin', language)}
                </Link>
              )}
            </nav>
          )}

          <div className="landing-nav-actions">
            {languageSelectorEnabled ? <LanguageSelector /> : null}
            {!user && (
              <Link to="/user" className="landing-nav-login" onClick={() => track('nav', 'click_login')}>
                {t('nav.login', language)}
              </Link>
            )}
            {user && (
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="dark"
                  id="site-header-user-menu"
                  className="landing-nav-user-dropdown d-flex align-items-center gap-2 border-0"
                >
                  {user.picture ? (
                    <Image src={user.picture} roundedCircle width={28} height={28} alt="" />
                  ) : (
                    <span
                      className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                      style={{ width: 28, height: 28, fontSize: '0.85rem' }}
                    >
                      {(user.name || user.email || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="d-none d-md-inline text-truncate" style={{ maxWidth: 140 }}>
                    {user.name || user.email}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="dark" align="end" className="landing-nav-user-menu">
                  <Dropdown.ItemText className="small text-secondary py-1">{user.email}</Dropdown.ItemText>
                  <Dropdown.Divider className="my-1 border-secondary" />
                  <Dropdown.Item as={Link} to="/user" className="py-2" onClick={() => track('nav', 'click_account')}>
                    {t('nav.account', language)}
                  </Dropdown.Item>
                  {isStaff && (
                    <Dropdown.Item
                      as={Link}
                      to={staffDashboardHref}
                      className="py-2"
                      onClick={() => track('nav', 'click_panel_menu')}
                    >
                      {t('nav.panel', language)}
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider className="my-1 border-secondary" />
                  <Dropdown.Item className="py-2" onClick={handleLogout}>
                    {t('common.logout', language)}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
            {whatsappBoliviaHref ? (
              <a
                href={whatsappBoliviaHref}
                target="_blank"
                rel="noreferrer"
                className="landing-nav-wa"
                onClick={() => track('nav', 'click_whatsapp')}
              >
                {waIcon}
                <span>{t('landing.navWhatsAppCta', language)}</span>
              </a>
            ) : null}
          </div>
        </Container>
      </header>

      {/* Mobile: slide-in panel (links + language + session + WhatsApp) */}
      <div
        className={`landing-nav-mobile-root ${mobileMenuOpen ? 'is-open' : ''}`}
        aria-hidden={!mobileMenuOpen}
      >
        <button
          type="button"
          tabIndex={mobileMenuOpen ? 0 : -1}
          className="landing-nav-mobile-backdrop"
          aria-label={t('common.close', language)}
          onClick={closeMobileMenu}
        />
        <div
          id="landing-nav-mobile-panel"
          className="landing-nav-mobile-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t('nav.menuToggleAria', language)}
        >
          <nav className="landing-nav-mobile-nav" aria-label="Primary">
            {variant === 'marketing'
              ? visibleMarketingNav.map((item) => (
                  <a
                    key={item.id}
                    href={`${hashPrefix}#${item.id}`}
                    className="landing-nav-mobile-link"
                    onClick={() => {
                      track('nav', `mobile_click_${item.id}`);
                      closeMobileMenu();
                    }}
                  >
                    {t(item.labelKey, language)}
                  </a>
                ))
              : (
                <>
                  <Link
                    to="/"
                    className="landing-nav-mobile-link"
                    onClick={() => {
                      track('nav', 'mobile_click_home');
                      closeMobileMenu();
                    }}
                  >
                    {t('nav.home', language)}
                  </Link>
                  <Link
                    to="/all-news"
                    className="landing-nav-mobile-link"
                    onClick={() => {
                      track('nav', 'mobile_click_all_news');
                      closeMobileMenu();
                    }}
                  >
                    {t('nav.allNews', language)}
                  </Link>
                  {activeAppSection !== 'dashboard' && isStaff && (
                    <Link
                      to={staffDashboardHref}
                      className="landing-nav-mobile-link landing-nav-mobile-link--highlight"
                      onClick={() => {
                        track('nav', 'mobile_click_dashboard');
                        closeMobileMenu();
                      }}
                    >
                      ✨ {t('nav.blogAdmin', language)}
                    </Link>
                  )}
                </>
                )}
          </nav>

          <div className="landing-nav-mobile-divider" />

          <div className="landing-nav-mobile-tools">
            {languageSelectorEnabled ? (
            <div className="landing-nav-mobile-tool-row">
              <LanguageSelector />
            </div>
            ) : null}
            {!user && (
              <Link
                to="/user"
                className="landing-nav-mobile-link landing-nav-mobile-link--cta"
                onClick={() => {
                  track('nav', 'mobile_click_login');
                  closeMobileMenu();
                }}
              >
                {t('nav.login', language)}
              </Link>
            )}
            {user && (
              <div className="landing-nav-mobile-user">
                <p className="landing-nav-mobile-user-email small mb-2">{user.email}</p>
                <Link
                  to="/user"
                  className="landing-nav-mobile-link"
                  onClick={() => {
                    track('nav', 'mobile_click_account');
                    closeMobileMenu();
                  }}
                >
                  {t('nav.account', language)}
                </Link>
                {isStaff && (
                  <Link
                    to={staffDashboardHref}
                    className="landing-nav-mobile-link landing-nav-mobile-link--highlight"
                    onClick={() => {
                      track('nav', 'mobile_click_panel');
                      closeMobileMenu();
                    }}
                  >
                    {t('nav.panel', language)}
                  </Link>
                )}
                <button type="button" className="landing-nav-mobile-link landing-nav-mobile-link--button" onClick={handleLogout}>
                  {t('common.logout', language)}
                </button>
              </div>
            )}
            {whatsappBoliviaHref ? (
              <a
                href={whatsappBoliviaHref}
                target="_blank"
                rel="noreferrer"
                className="landing-nav-wa landing-nav-mobile-wa"
                onClick={() => {
                  track('nav', 'mobile_click_whatsapp');
                  closeMobileMenu();
                }}
              >
                {waIcon}
                <span>{t('landing.navWhatsAppCta', language)}</span>
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SiteHeader;
